import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CalendarDays, Columns3, List, Plus, Search } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Pagination } from '../../../shared/components/Pagination';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import { useEtapas, useOportunidades } from '../hooks';
import { useOportunidadeFilters } from '../hooks/useOportunidadeFilters';
import { OportunidadeCard } from '../components/OportunidadeCard';
import type {
  ListOportunidadesParams,
  OportunidadeStatus,
  Prioridade,
} from '../api';

const DEFAULT_PAGE_SIZE = 24;

const STATUS_OPTIONS: { value: OportunidadeStatus; label: string }[] = [
  { value: 'aberta', label: 'Aberta' },
  { value: 'ganha', label: 'Ganha' },
  { value: 'perdida', label: 'Perdida' },
  { value: 'suspensa', label: 'Suspensa' },
  { value: 'cancelada', label: 'Cancelada' },
];

const PRIORIDADE_OPTIONS: { value: Prioridade; label: string }[] = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'media', label: 'Média' },
  { value: 'alta', label: 'Alta' },
  { value: 'critica', label: 'Crítica' },
];

/**
 * Visão Cards (US-COM-004 §Filtros e Visões, task 14.1). Grid
 * responsivo dos mesmos dados da lista — reutiliza `useOportunidades`
 * e compartilha filtros via `useOportunidadeFilters` (query string),
 * então alternar entre Lista/Cards/Calendário preserva o contexto.
 */
export default function OportunidadesCardsPage(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { filters, setFilter } = useOportunidadeFilters();
  const [searchTerm, setSearchTerm] = useState(filters.search ?? '');
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = searchTerm.trim();
      if ((filters.search ?? '') !== trimmed) setFilter('search', trimmed || undefined);
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm, filters.search, setFilter]);

  useEffect(() => {
    setOffset(0);
  }, [filters.search, filters.status, filters.prioridade, filters.stageId]);

  const params = useMemo<ListOportunidadesParams>(
    () => ({
      search: filters.search,
      status: filters.status,
      prioridade: filters.prioridade,
      stageId: filters.stageId,
      limit,
      offset,
    }),
    [filters, limit, offset],
  );

  const { data, isLoading, isError, error } = useOportunidades(params);
  const { data: stages } = useEtapas(true);

  const items = data?.data ?? [];
  const count = data?.pagination?.count ?? 0;

  const stageById = useMemo(() => {
    const m = new Map<string, string>();
    (stages ?? []).forEach((s) => m.set(s.id, s.label));
    return m;
  }, [stages]);

  return (
    <div className="p-6 space-y-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Oportunidades — visão Cards
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Cartões compactos com foco em prioridade, valor e status.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate(`/oportunidades${location.search}`)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <List className="h-4 w-4" />
            Lista
          </button>
          <button
            onClick={() => navigate('/oportunidades/kanban')}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Columns3 className="h-4 w-4" />
            Kanban
          </button>
          <button
            onClick={() => navigate('/oportunidades/calendario')}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <CalendarDays className="h-4 w-4" />
            Calendário
          </button>
          <button
            onClick={() => navigate('/oportunidades/nova')}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nova oportunidade
          </button>
        </div>
      </div>

      {/* Filtros — mesmo shape da Lista, hooked no mesmo useOportunidadeFilters */}
      <div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="md:col-span-2">
            <TextInput
              label="Buscar"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={<Search className="h-4 w-4 text-gray-400" />}
              aria-label="Buscar oportunidades por código, nome ou produto"
            />
          </div>
          <AutocompleteSelect
            label="Status"
            options={[
              { value: '', label: 'Todos os status' },
              ...STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
            ]}
            value={filters.status ?? ''}
            onChange={(v) => setFilter('status', v || undefined)}
          />
          <AutocompleteSelect
            label="Prioridade"
            options={[
              { value: '', label: 'Todas as prioridades' },
              ...PRIORIDADE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
            ]}
            value={filters.prioridade ?? ''}
            onChange={(v) => setFilter('prioridade', v || undefined)}
          />
          <div className="md:col-span-4">
            <div className="md:w-1/3">
              <AutocompleteSelect
                label="Etapa"
                options={[
                  { value: '', label: 'Todas as etapas' },
                  ...((stages ?? []).filter((s) => s.active).map((s) => ({ value: s.id, label: s.label }))),
                ]}
                value={filters.stageId ?? ''}
                onChange={(v) => setFilter('stageId', v || undefined)}
              />
            </div>
          </div>
        </div>
      </div>

      {isError && (
        <Alert variant="error" className="mb-4">
          Erro ao carregar oportunidades:{' '}
          {(error as Error)?.message ?? 'Falha desconhecida.'}
        </Alert>
      )}

      {isLoading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <EmptyState
          title="Nenhuma oportunidade encontrada"
          description="Ajuste os filtros ou cadastre uma nova."
        />
      )}

      {!isLoading && items.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((o) => (
            <OportunidadeCard
              key={o.id}
              card={o}
              stageLabel={stageById.get(o.stageId)}
            />
          ))}
        </div>
      )}

      <div className="mt-4">
        <Pagination
          offset={offset}
          limit={limit}
          count={count}
          itemLabel="oportunidade"
          itemLabelPlural="oportunidades"
          pageSizeOptions={[12, 24, 48]}
          onChange={(next) => {
            setLimit(next.limit);
            setOffset(next.offset);
          }}
        />
      </div>
    </div>
  );
}
